/**
 * AR Email Intelligence Demo — scene checkpoint helpers (E0 / GEN-2262).
 *
 * THROWAWAY DEMO CODE. One cumulative, IDEMPOTENT state-producer per scene
 * transition. Each mutates the shared fixture store via the existing
 * `updateArDemoState(producer)` mechanism (see `../fixture-store.tsx`).
 *
 * Contract for E1–E4:
 *  - Every function is safe to call REPEATEDLY (idempotent) — re-running a scene
 *    leaves state unchanged.
 *  - Every function is safe to call OUT OF ORDER where reasonable, so a presenter
 *    can drop into any scene. Later checkpoints do not depend on earlier ones
 *    having run, EXCEPT where the narrative strictly requires it (the reply only
 *    makes sense after the send, so `applyReplyReceived` also marks the email
 *    sent — keeping it self-sufficient).
 *  - Reset (`resetArDemoState`, structuredClone of the cold-open seed) returns to
 *    the cold-open state: corpus NOT shown, no email sent, no reply, no rec
 *    applied. None of these helpers touch the seed, so reset always wins.
 */

import { updateArDemoState } from '../fixture-store';

/** S0 — reveal the corpus + mark the voice profile extracted. */
export function applyCorpusLearned(): void {
  updateArDemoState((draft) => {
    draft.emailIntel.corpus.voiceProfileExtracted = true;
  });
}

/**
 * S1 — show the voice profile + per-customer comms profiles. The data is always
 * present in the seed; this checkpoint guarantees the corpus gate is open so the
 * profiles render even if the presenter jumps straight to Scene 1.
 */
export function applyProfilesShown(): void {
  updateArDemoState((draft) => {
    draft.emailIntel.corpus.voiceProfileExtracted = true;
  });
}

/**
 * S2 (hero) — the voice-matched Crestline draft is generated. The draft strings
 * are static in the seed; opening the corpus gate is the only state change, so
 * jumping straight here still reads correctly.
 */
export function applyVoiceDraftGenerated(): void {
  updateArDemoState((draft) => {
    draft.emailIntel.corpus.voiceProfileExtracted = true;
  });
}

/**
 * S3 — tone variants for Granite Peak + Cascade are shown. Variant strings are
 * static; opening the corpus gate keeps the scene self-sufficient.
 */
export function applyToneVariants(): void {
  updateArDemoState((draft) => {
    draft.emailIntel.corpus.voiceProfileExtracted = true;
  });
}

/**
 * S4 — customer intelligence (learned facts + provenance) is shown for Granite
 * Peak. Facts are static in the seed; opening the corpus gate keeps the scene
 * self-sufficient.
 */
export function applyCustomerIntelligenceShown(): void {
  updateArDemoState((draft) => {
    draft.emailIntel.corpus.voiceProfileExtracted = true;
  });
}

/**
 * S5 — apply the Granite Peak recommendations (mark each `applied`). Idempotent:
 * re-running leaves already-applied recs untouched.
 */
export function applyRecommendationApplied(): void {
  updateArDemoState((draft) => {
    draft.emailIntel.corpus.voiceProfileExtracted = true;
    for (const rec of draft.emailIntel.recommendations) {
      rec.applied = true;
    }
  });
}

/** S6a — the Granite Peak email is sent. Idempotent. */
export function applyEmailSent(): void {
  updateArDemoState((draft) => {
    draft.emailIntel.corpus.voiceProfileExtracted = true;
    draft.emailIntel.emailSent = true;
  });
}

/**
 * S6b — the warm inbound reply lands. Self-sufficient: also marks the email as
 * sent so jumping straight to the reply still reads correctly. Idempotent.
 */
export function applyReplyReceived(): void {
  updateArDemoState((draft) => {
    draft.emailIntel.corpus.voiceProfileExtracted = true;
    draft.emailIntel.emailSent = true;
    draft.emailIntel.reply.received = true;
  });
}
